"use client";
import { useTranslations } from "next-intl";
import { Track, ParticipantEvent } from "livekit-client";
import { memo, useEffect, useState, useRef } from "react";
import { Mic, Pin, User, PinOff, MicOff, Sparkles, Video, VideoOff, UserX, X } from "lucide-react";
import { VideoTrack, useIsSpeaking, TrackReferenceOrPlaceholder, TrackReference } from "@livekit/components-react";

export interface ParticipantTileProps {
  track: TrackReferenceOrPlaceholder;
  isLocal: boolean;
  isHandRaised: boolean;
  handQueuePosition?: number | null;
  canModerateLowerHand?: boolean;
  onLowerParticipantHand?: (participantSid: string) => void;
  onPin: () => void;
  isPinned: boolean;
  onToggleSettings?: () => void;
  disableHoverControls?: boolean;
  canModerate?: boolean;
  canRequestUnmute?: boolean;
  onMuteParticipant?: (participantIdentity: string) => void;
  onUnmuteParticipant?: (participantIdentity: string) => void;
  onMuteParticipantVideo?: (participantIdentity: string) => void;
  onUnmuteParticipantVideo?: (participantIdentity: string) => void;
  onKickParticipant?: (participantIdentity: string) => void;
  avatarUrl?: string | null;
}

/** Track events to listen for mute state changes */
const TRACK_EVENTS = [
  ParticipantEvent.TrackMuted,
  ParticipantEvent.TrackUnmuted,
  ParticipantEvent.TrackPublished,
  ParticipantEvent.TrackUnpublished,
  ParticipantEvent.LocalTrackPublished,
  ParticipantEvent.LocalTrackUnpublished,
  ParticipantEvent.TrackSubscribed,
  ParticipantEvent.TrackUnsubscribed
] as const;

/** ParticipantTile - Renders individual video/audio/placeholder tile */
export const ParticipantTile = memo(function ParticipantTile({
  track,
  isLocal,
  isHandRaised,
  handQueuePosition,
  canModerateLowerHand = false,
  onLowerParticipantHand,
  onPin,
  isPinned,
  onToggleSettings,
  disableHoverControls,
  canModerate = false,
  canRequestUnmute = false,
  onMuteParticipant,
  onUnmuteParticipant,
  onMuteParticipantVideo,
  onUnmuteParticipantVideo,
  onKickParticipant,
  avatarUrl
}: ParticipantTileProps) {
  const t = useTranslations();
  const isSpeaking = useIsSpeaking(track.participant);
  const containerRef = useRef<HTMLDivElement>(null);

  const [muteState, setMuteState] = useState(() => ({
    audio: getAudioMutedState(track.participant),
    video: getVideoMutedState(track.participant)
  }));

  const [showControls, setShowControls] = useState(false);
  const controlsTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const handleMouseMove = () => {
    setShowControls(true);
    if (controlsTimeoutRef.current) {
      clearTimeout(controlsTimeoutRef.current);
    }
    controlsTimeoutRef.current = setTimeout(() => {
      setShowControls(false);
    }, 1000);
  };

  const handleMouseLeave = () => {
    if (controlsTimeoutRef.current) {
      clearTimeout(controlsTimeoutRef.current);
    }
    setShowControls(false);
  };

  useEffect(() => {
    const participant = track.participant;
    if (!participant) return;

    const updateMuteStates = () => {
      setMuteState((prev) => {
        const newAudio = getAudioMutedState(participant);
        const newVideo = getVideoMutedState(participant);
        return prev.audio === newAudio && prev.video === newVideo ? prev : { audio: newAudio, video: newVideo };
      });
    };

    updateMuteStates();
    const timeoutId = setTimeout(updateMuteStates, 200);

    TRACK_EVENTS.forEach((event) => participant.on(event, updateMuteStates));

    return () => {
      clearTimeout(timeoutId);
      TRACK_EVENTS.forEach((event) => participant.off(event, updateMuteStates));
    };
  }, [track.participant, track.publication?.isMuted, track.publication?.track]);

  const participantName = track.participant.name || track.participant.identity;
  const participantIdentity = track.participant.identity;
  const showModerationActions = canModerate && !isLocal;
  const showAudioModeration =
    showModerationActions && onMuteParticipant && onUnmuteParticipant && (!muteState.audio || canRequestUnmute);
  const showVideoModeration =
    showModerationActions &&
    onMuteParticipantVideo &&
    onUnmuteParticipantVideo &&
    (!muteState.video || canRequestUnmute);
  const showKickAction = showModerationActions && onKickParticipant;
  const isScreenShare = track.source === Track.Source.ScreenShare;
  const isVideoEnabled = isScreenShare
    ? !!track.publication?.track
    : track.publication && !track.publication.isMuted && !muteState.video;
  const hasVideoTrack = track.publication?.track;

  const speakingRing = isSpeaking && !isHandRaised;
  const tileRingClass = isHandRaised
    ? "ring-2 ring-amber-400 shadow-[0_0_20px_rgba(251,191,36,0.15)]"
    : speakingRing
      ? "ring-2 ring-(--primary)"
      : "ring-0";

  return (
    <div
      ref={containerRef}
      className={`relative flex h-full w-full items-center justify-center overflow-hidden rounded-xl bg-[#161f2e] transition-all duration-200 ${tileRingClass}`}
      role="article"
      aria-label={t("participant.video", { name: `${participantName}${isLocal ? t("participant.youSuffix") : ""}` })}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
    >
      {hasVideoTrack && (
        <VideoTrack
          trackRef={track as TrackReference}
          className={`absolute inset-0 h-full w-full object-cover ${isLocal && !isScreenShare ? "scale-x-[-1]" : ""}`}
        />
      )}

      {!isVideoEnabled && (
        <div className="absolute inset-0 flex flex-col items-center justify-center bg-linear-to-br from-gray-800 to-gray-900">
          <div className="relative">
            <div
              className={`flex h-16 w-16 items-center justify-center overflow-hidden rounded-full text-xl font-semibold text-white shadow-lg ring-4 transition-all duration-200 md:h-20 md:w-20 md:text-2xl lg:h-24 lg:w-24 lg:text-4xl ${
                isHandRaised
                  ? "bg-gray-600 ring-amber-400/40"
                  : isSpeaking
                    ? "scale-110 bg-(--primary) ring-(--primary)/30"
                    : "bg-gray-600 ring-gray-700"
              }`}
            >
              {avatarUrl ? (
                // eslint-disable-next-line @next/next/no-img-element -- Odoo CDN avatar URLs are external and dynamic
                <img src={avatarUrl} alt="" className="h-full w-full object-cover" referrerPolicy="no-referrer" />
              ) : participantName[0]?.toUpperCase() ? (
                participantName[0].toUpperCase()
              ) : (
                <User className="h-10 w-10" />
              )}
            </div>
            {isHandRaised ? (
              <span
                className="absolute -bottom-1 -right-1 flex h-7 w-7 items-center justify-center rounded-full bg-amber-400 text-sm text-slate-950 shadow-md ring-2 ring-[#161f2e]"
                aria-hidden
              >
                <span className="animate-hand-wave leading-none">✋</span>
              </span>
            ) : null}
          </div>
          {isLocal && <span className="mt-4 hidden text-xs text-white/50 lg:block">{t("participant.youSuffix")}</span>}
        </div>
      )}

      {isHandRaised ? (
        <div
          className="absolute top-3 left-3 z-30 flex max-w-[calc(100%-1.5rem)] items-center gap-1.5 rounded-full bg-amber-400 py-1 pl-2 pr-1.5 text-xs font-semibold text-slate-950 shadow-lg"
          role="status"
          aria-label={t("participant.handRaised", { defaultValue: "Hand raised" })}
        >
          <span className="text-base leading-none animate-hand-wave" aria-hidden>
            ✋
          </span>
          {handQueuePosition != null && handQueuePosition > 0 ? (
            <span className="rounded-full bg-slate-950/15 px-1.5 py-0.5 text-[10px] font-bold">
              #{handQueuePosition}
            </span>
          ) : null}
          {canModerateLowerHand && !isLocal && onLowerParticipantHand ? (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onLowerParticipantHand(track.participant.sid);
              }}
              className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-slate-950/20 text-slate-950 transition hover:bg-slate-950/30"
              title={t("participantsSidebar.lowerHand")}
              aria-label={t("participantsSidebar.lowerHand")}
            >
              <X className="h-3 w-3" aria-hidden />
            </button>
          ) : null}
        </div>
      ) : null}

      {!disableHoverControls && (
        <div
          className={`pointer-events-none absolute inset-0 z-10 transition-opacity duration-200 ${
            showControls ? "opacity-100" : "opacity-0"
          }`}
        >
          <div className="pointer-events-auto absolute top-1/2 left-1/2 flex -translate-x-1/2 -translate-y-1/2 flex-col items-center">
            <div className="z-20 flex max-w-[min(100%,20rem)] flex-wrap items-center justify-center gap-1 rounded-full bg-zinc-700/60 px-2 py-2 shadow-lg backdrop-blur-md sm:gap-2 sm:px-3">
              <button
                className={`rounded-full p-2 text-white/50 transition-colors duration-200 hover:bg-white/10 ${isPinned ? "bg-white/20" : ""}`}
                title={isPinned ? t("participant.unpinFromScreen") : t("participant.pinToScreen")}
                aria-label={isPinned ? t("participant.unpinFromScreen") : t("participant.pinToScreen")}
                onClick={(e) => {
                  e.stopPropagation();
                  onPin();
                }}
              >
                {isPinned ? (
                  <PinOff className="h-5 w-5" aria-hidden="true" />
                ) : (
                  <Pin className="h-5 w-5" aria-hidden="true" />
                )}
              </button>

              {showAudioModeration ? (
                <button
                  type="button"
                  className={`rounded-full p-2 transition-colors duration-200 hover:bg-white/10 ${
                    muteState.audio ? "text-emerald-300" : "text-white/70"
                  }`}
                  title={muteState.audio ? t("participant.requestAudio") : t("participant.muteAudio")}
                  aria-label={muteState.audio ? t("participant.requestAudio") : t("participant.muteAudio")}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (muteState.audio) {
                      onUnmuteParticipant!(participantIdentity);
                    } else {
                      onMuteParticipant!(participantIdentity);
                    }
                  }}
                >
                  {muteState.audio ? (
                    <Mic className="h-5 w-5" aria-hidden="true" />
                  ) : (
                    <MicOff className="h-5 w-5" aria-hidden="true" />
                  )}
                </button>
              ) : null}

              {showVideoModeration ? (
                <button
                  type="button"
                  className={`rounded-full p-2 transition-colors duration-200 hover:bg-white/10 ${
                    muteState.video ? "text-emerald-300" : "text-white/70"
                  }`}
                  title={muteState.video ? t("participant.requestVideo") : t("participant.muteVideo")}
                  aria-label={muteState.video ? t("participant.requestVideo") : t("participant.muteVideo")}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (muteState.video) {
                      onUnmuteParticipantVideo!(participantIdentity);
                    } else {
                      onMuteParticipantVideo!(participantIdentity);
                    }
                  }}
                >
                  {muteState.video ? (
                    <Video className="h-5 w-5" aria-hidden="true" />
                  ) : (
                    <VideoOff className="h-5 w-5" aria-hidden="true" />
                  )}
                </button>
              ) : null}

              {showKickAction ? (
                <button
                  type="button"
                  className="rounded-full p-2 text-red-300/90 transition-colors duration-200 hover:bg-red-500/20"
                  title={t("participant.removeFromMeeting")}
                  aria-label={t("participant.removeFromMeeting")}
                  onClick={(e) => {
                    e.stopPropagation();
                    onKickParticipant!(participantIdentity);
                  }}
                >
                  <UserX className="h-5 w-5" aria-hidden="true" />
                </button>
              ) : null}

              {isLocal && (
                <button
                  className="rounded-full p-2 text-white/50 transition-colors duration-200 hover:bg-white/10"
                  title={t("participant.visualEffects")}
                  aria-label={t("participant.visualEffects")}
                  onClick={(e) => {
                    e.stopPropagation();
                    onToggleSettings?.();
                  }}
                >
                  <Sparkles className="h-5 w-5" aria-hidden="true" />
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      <div className="absolute bottom-2 left-2 z-20 flex w-max max-w-[calc(100%-1rem)] items-center gap-1.5 rounded-lg bg-black/50 px-2.5 py-1.5 text-xs text-white backdrop-blur-xs">
        {muteState.audio ? (
          <MicOff className="h-3.5 w-3.5 shrink-0 text-red-400" />
        ) : (
          <Mic className="h-3.5 w-3.5 shrink-0 text-green-400" />
        )}
        <span className="truncate">
          {participantName}
          {isLocal && t("participant.youSuffix")}
        </span>
        {!isVideoEnabled && <VideoOff className="h-3.5 w-3.5 shrink-0 text-white/60" />}
      </div>
    </div>
  );
});

export const getAudioMutedState = (participant: TrackReferenceOrPlaceholder["participant"]): boolean => {
  if (!participant) return true;
  if (participant.isLocal) {
    if (participant.isMicrophoneEnabled) return false;
    const audioTracks = Array.from(participant.audioTrackPublications.values());
    if (audioTracks.some((pub) => !pub.isMuted)) return false;
  }
  return !participant.isMicrophoneEnabled;
};

export const getVideoMutedState = (participant: TrackReferenceOrPlaceholder["participant"]): boolean => {
  if (!participant) return true;
  return !participant.isCameraEnabled;
};
